import { rtLogService } from '@rammetec/core';
import { repositoryResolver } from '@rammetec/data-access';
import { PRODUCT_VARIANT, PRODUCT_VARIANT_MAX_OPTIONS, } from '../types/product-variant.js';
import { PRODUCT } from '../types/product.js';
import { TECHNICAL_SHEET } from '../types/technical-sheet.js';
import { COLOR } from '../types/color.js';
import { VARIATION_OPTION } from '../types/variation-option.js';
import { SIZE_OCCURRENCE } from '../types/size-occurrence.js';
import { auditService } from './auditService.js';
/** Sentinel used in the key for empty/absent slots. */
const EMPTY_SLOT = '0';
/** Sentinel used when `technicalSheetId` is absent (sheet-less variant). */
const NO_SHEET = 'none';
/**
 * Generates the deterministic variant key. **Pure** — no I/O.
 *
 * Key format:
 * `${productId}|${sheetOrNone}|${colorOrNone}|${sizeOrNone}|${opt1}|${opt2}|${opt3}|${opt4}|${opt5}`
 *
 * - `technicalSheetId` absent → `'none'`
 * - `colorId` / `sizeOccurrenceId` absent → `'0'`
 * - `variationOptionIds` sorted, padded to {@link PRODUCT_VARIANT_MAX_OPTIONS} with `'0'`
 * - `sizeGridId` is **not** part of the key (R1)
 */
export function generateVariantKey(productId, technicalSheetId, values) {
    const sheetPart = technicalSheetId || NO_SHEET;
    const colorPart = values.colorId || EMPTY_SLOT;
    const sizePart = values.sizeOccurrenceId || EMPTY_SLOT;
    const sorted = [...(values.variationOptionIds ?? [])]
        .filter((id) => !!id)
        .sort((a, b) => a.localeCompare(b));
    const slots = Array.from({ length: PRODUCT_VARIANT_MAX_OPTIONS }, (_, i) => sorted[i] ?? EMPTY_SLOT);
    return [productId, sheetPart, colorPart, sizePart, ...slots].join('|');
}
/** Variant key of the partial (size slot zeroed) for the same identifying composition. */
export function partialVariantKey(productId, technicalSheetId, values) {
    return generateVariantKey(productId, technicalSheetId, {
        ...values,
        sizeOccurrenceId: null,
    });
}
/**
 * Partial key from a **full** variant-key string: zeros the size slot (index 3 of
 * `productId|sheet|color|size|opt1..5`). Pure string op — no I/O.
 * Idempotent on an already-partial key. Contract of the key format is fixed
 * (0097 / `generateVariantKey`); keep a regression test when changing.
 */
export function partialVariantKeyOf(fullVariantKey) {
    const parts = fullVariantKey.split('|');
    if (parts.length < 4)
        return fullVariantKey;
    parts[3] = EMPTY_SLOT;
    return parts.join('|');
}
/** True when the size slot is empty (`'0'` / null) — identifying-only variant. */
export function isPartialVariant(variant) {
    return !(variant.sizeOccurrenceId?.trim());
}
/** True when the size slot is filled — stock-level variant. */
export function isFullVariant(variant) {
    return !!variant.sizeOccurrenceId?.trim();
}
/**
 * Composes a friendly description from the product label and variation value labels.
 * Pure. Example: `("Tecido X", ["Vermelho", "Antifungo"])` → `"Tecido X - Vermelho - Antifungo"`.
 */
export function composeDescription(productLabel, valueLabels) {
    return [productLabel, ...valueLabels.filter((l) => !!l)].join(' - ');
}
/**
 * Domain service for the org-scoped `ProductVariant` entity
 * (org/{orgId}/product-variant).
 *
 * A ProductVariant is a **technical** entity — generated on demand by the resolver
 * (0012/0015), not created manually. This service owns persistence/dedup and read
 * access; it does **not** validate composition integrity (that product/sheet exist,
 * that color/options/size are valid) — that is the resolver's responsibility (0015/0097).
 * See {@link findOrCreateVariant}.
 */
class ProductVariantService {
    getRepository(orgId) {
        return repositoryResolver.resolveWithContext(PRODUCT_VARIANT, { orgId });
    }
    /** All variants for an organization. */
    async getByOrganization(orgId) {
        try {
            const repository = await this.getRepository(orgId);
            return repository.getAll();
        }
        catch (error) {
            rtLogService.error('Error fetching product variants', { orgId, error });
            throw error;
        }
    }
    /** Find a variant by id (= variantKey). */
    async findById(orgId, id) {
        try {
            const repository = await this.getRepository(orgId);
            return repository.getById(id);
        }
        catch (error) {
            rtLogService.error('Error fetching product variant by id', { orgId, id, error });
            throw error;
        }
    }
    /** All variants of a given product. */
    async findByProduct(orgId, productId) {
        const all = await this.getByOrganization(orgId);
        return all.filter((v) => v.productId === productId);
    }
    /** Find a variant by its deterministic key. Since `id === variantKey`, this is `getById`. */
    async findByVariantKey(orgId, key) {
        return this.findById(orgId, key);
    }
    /**
     * Ids of SizeOccurrence, among those informed, that are already used by some
     * variant in the org. One read; the variant catalog is small while creation is
     * lazy (request 0035). Follow-up: indexed `filterAst` when volume grows.
     */
    async findUsedSizeOccurrenceIds(orgId, ids) {
        if (ids.length === 0)
            return new Set();
        const wanted = new Set(ids.filter(Boolean));
        if (wanted.size === 0)
            return new Set();
        const all = await this.getByOrganization(orgId);
        const used = new Set();
        for (const v of all) {
            if (v.sizeOccurrenceId && wanted.has(v.sizeOccurrenceId)) {
                used.add(v.sizeOccurrenceId);
            }
        }
        return used;
    }
    /**
     * Returns the existing variant for the composition, creating it if absent.
     *
     * **Persistence / dedup only.** This method does NOT validate that the product/sheet
     * exist or that color/options/size are valid — that integrity check belongs to the
     * resolver. It sorts option ids, generates the key, and upserts using `id === variantKey`
     * so uniqueness is atomic and DB-agnostic.
     *
     * `sizeGridId` is persisted (R1) but does **not** enter the key.
     */
    async findOrCreateVariant(orgId, input) {
        try {
            const values = input.values ?? {};
            const sorted = [...(values.variationOptionIds ?? [])]
                .filter((id) => !!id)
                .sort((a, b) => a.localeCompare(b));
            const sizeOccurrenceId = values.sizeOccurrenceId ?? null;
            const normalizedValues = {
                colorId: values.colorId ?? null,
                sizeOccurrenceId,
                variationOptionIds: sorted,
            };
            const key = generateVariantKey(input.productId, input.technicalSheetId, normalizedValues);
            const repository = await this.getRepository(orgId);
            const existing = await repository.getById(key);
            if (existing) {
                // Backfill parent link when an older full row lacked it (pre-0106 / race).
                if (sizeOccurrenceId &&
                    input.parentVariantId &&
                    !existing.parentVariantId) {
                    return repository.update(key, auditService.stampOnUpdate({
                        parentVariantId: input.parentVariantId,
                    }));
                }
                return existing;
            }
            const sizeGridId = sizeOccurrenceId ? (input.sizeGridId ?? null) : null;
            const parentVariantId = sizeOccurrenceId
                ? (input.parentVariantId ?? null)
                : null;
            const entity = {
                id: key,
                productId: input.productId,
                technicalSheetId: input.technicalSheetId ?? null,
                colorId: normalizedValues.colorId ?? null,
                sizeOccurrenceId,
                sizeGridId,
                parentVariantId,
                variationOptionId1: sorted[0] ?? null,
                variationOptionId2: sorted[1] ?? null,
                variationOptionId3: sorted[2] ?? null,
                variationOptionId4: sorted[3] ?? null,
                variationOptionId5: sorted[4] ?? null,
                variantKey: key,
                description: input.description ?? null,
                active: true,
            };
            const created = await repository.upsert(auditService.stampOnCreate(entity));
            rtLogService.info('ProductVariant created', { orgId, variantKey: key });
            return created;
        }
        catch (error) {
            rtLogService.error('Error in findOrCreateVariant', { orgId, error });
            throw error;
        }
    }
    /**
     * Loads the entities referenced by a variant's **values**: product, technical sheet
     * (if any), color (if any), size occurrence (if any), and variation options.
     * Size occurrence is resolved via `variant.sizeGridId` (R1).
     */
    async getComposition(orgId, id) {
        try {
            const variant = await this.findById(orgId, id);
            if (!variant) {
                return {
                    product: null,
                    technicalSheet: null,
                    color: null,
                    sizeOccurrence: null,
                    variationOptions: [],
                };
            }
            const [productRepo, sheetRepo, colorRepo, variationOptionRepo] = await Promise.all([
                repositoryResolver.resolveWithContext(PRODUCT, { orgId }),
                repositoryResolver.resolveWithContext(TECHNICAL_SHEET, { orgId }),
                repositoryResolver.resolveWithContext(COLOR, { orgId }),
                repositoryResolver.resolveWithContext(VARIATION_OPTION, { orgId }),
            ]);
            const optionIds = [
                variant.variationOptionId1,
                variant.variationOptionId2,
                variant.variationOptionId3,
                variant.variationOptionId4,
                variant.variationOptionId5,
            ].filter((oid) => !!oid);
            const sizePromise = variant.sizeOccurrenceId && variant.sizeGridId
                ? repositoryResolver
                    .resolveWithContext(SIZE_OCCURRENCE, {
                    orgId,
                    sizeGridId: variant.sizeGridId,
                })
                    .then((repo) => repo.getById(variant.sizeOccurrenceId))
                    .catch(() => null)
                : Promise.resolve(null);
            const [product, technicalSheet, color, sizeOccurrence, variationOptionResults] = await Promise.all([
                productRepo.getById(variant.productId),
                variant.technicalSheetId
                    ? sheetRepo.getById(variant.technicalSheetId)
                    : Promise.resolve(null),
                variant.colorId ? colorRepo.getById(variant.colorId) : Promise.resolve(null),
                sizePromise,
                Promise.all(optionIds.map((oid) => variationOptionRepo.getById(oid))),
            ]);
            return {
                product,
                technicalSheet,
                color,
                sizeOccurrence,
                variationOptions: variationOptionResults.filter((o) => !!o),
            };
        }
        catch (error) {
            rtLogService.error('Error loading product variant composition', { orgId, id, error });
            throw error;
        }
    }
    /** Soft-disable a variant (maintenance). Variants are referenced, never deleted. */
    async disableVariant(orgId, id) {
        try {
            const repository = await this.getRepository(orgId);
            const patch = { active: false };
            await repository.update(id, auditService.stampOnUpdate(patch));
            rtLogService.info('ProductVariant disabled', { orgId, variantKey: id });
        }
        catch (error) {
            rtLogService.error('Error disabling product variant', { orgId, id, error });
            throw error;
        }
    }
}
export const productVariantService = new ProductVariantService();
//# sourceMappingURL=productVariantService.js.map